import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  FiCheck,
  FiClock,
  FiCornerUpLeft,
  FiLink,
  FiMessageCircle,
  FiRotateCcw,
} from 'react-icons/fi';
import { sanitizeHtml } from '../../utils/safeHtml';
import {
  applyThreadHighlights,
  captureTextSelection,
  groupReviewThreads,
} from './reviewThreads';
import './ReviewWorkspace.css';

const looksLikeHtml = (value = '') => /<\/?[a-z][\s\S]*>/i.test(String(value));
const REACTIONS = ['👍', '❤️', '🎉', '👀', '❗'];

const ROLE_META = {
  student: { label: 'Student', tone: 'student' },
  coach: { label: 'Coach', tone: 'coach' },
  ai: { label: 'Liwaza AI', tone: 'ai' },
  notes: { label: 'Note', tone: 'note' },
};

const roleMeta = (authorType) => ROLE_META[authorType] || { label: 'Comment', tone: 'note' };

const displayName = (item) => {
  const name = String(item?.author_name || '').trim();
  if (name) {
    return name;
  }
  return roleMeta(item?.author_type).label;
};

const initialsOf = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) {
    return '•';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const formatWhen = (iso) => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const diff = Date.now() - date.getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) {
    return 'Just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.round(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  return date.toLocaleDateString();
};

const mentionHandle = (name) =>
  String(name || '')
    .replace(/\s+/g, '');

export const mentionedIdsFromText = (text, collaborators = []) => {
  const ids = [];
  const value = String(text || '');
  collaborators.forEach((person) => {
    const name = String(person.name || '').trim();
    if (!name) {
      return;
    }
    const handle = `@${mentionHandle(name)}`;
    const first = `@${name.split(/\s+/)[0]}`;
    if (value.includes(handle) || value.includes(`@${name}`) || value.includes(first)) {
      ids.push(person.id);
    }
  });
  return ids;
};

const MentionField = ({ value, onChange, collaborators = [], placeholder }) => {
  const at = value.lastIndexOf('@');
  const token = at >= 0 ? value.slice(at + 1) : '';
  const open = at >= 0 && token.length >= 0 && !/\s/.test(token);
  const matches = open
    ? collaborators.filter((person) =>
        String(person.name || '')
          .toLowerCase()
          .includes(token.toLowerCase())
      )
    : [];
  return (
    <div className="rw-mention">
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
      {matches.length ? (
        <ul className="rw-mention__list">
          {matches.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                onClick={() =>
                  onChange(`${value.slice(0, at)}@${mentionHandle(person.name)} `)
                }
              >
                {person.name}
                <span>{person.role}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const CommentMessage = ({ item }) => {
  const name = displayName(item);
  const role = roleMeta(item.author_type);
  const when = formatWhen(item.created_at);
  return (
    <div className={`rw-msg is-${role.tone}`}>
      <span className={`rw-avatar is-${role.tone}`} aria-hidden="true">
        {initialsOf(name)}
      </span>
      <div className="rw-msg__main">
        <div className="rw-msg__meta">
          <strong>{name}</strong>
          <span className={`rw-role is-${role.tone}`}>{role.label}</span>
          {when ? <time dateTime={item.created_at}>{when}</time> : null}
        </div>
        <p className="rw-msg__body">{item.comment}</p>
      </div>
    </div>
  );
};

const ReviewWorkspace = ({
  body = '',
  comments = [],
  selectedCommentId = null,
  canComment = true,
  canReturn = false,
  returningLabel = 'Return to student',
  onSubmitComment,
  onResolve,
  onReopen,
  onReact,
  onAssign,
  onAcceptSuggestion,
  onRejectSuggestion,
  onRestoreVersion,
  collaborators = [],
  versions = [],
  versionsLoading = false,
  currentUserId = null,
  saving = false,
  error = '',
  focusComments = false,
  pendingExcerpt = '',
  layout = 'split',
}) => {
  const bodyRef = useRef(null);
  const documentRef = useRef(null);
  const sidebarRef = useRef(null);
  const [mobileTab, setMobileTab] = useState('document');
  const [draft, setDraft] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [suggestionDraft, setSuggestionDraft] = useState('');
  const [assignDraft, setAssignDraft] = useState('');
  const [mode, setMode] = useState('edit');
  const [selection, setSelection] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [activeId, setActiveId] = useState(selectedCommentId);
  const [replyTo, setReplyTo] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(null);

  const people = useMemo(() => {
    if (collaborators.length) {
      return collaborators;
    }
    const seen = new Map();
    comments.forEach((item) => {
      if (item.author_user_id && !seen.has(item.author_user_id)) {
        seen.set(item.author_user_id, {
          id: item.author_user_id,
          name: item.author_name || roleMeta(item.author_type).label,
          role: item.author_type,
        });
      }
    });
    return Array.from(seen.values());
  }, [collaborators, comments]);

  const threads = useMemo(() => groupReviewThreads(comments), [comments]);
  const unresolved = threads.filter((thread) => !thread.resolved_at).length;
  const pendingSuggestions = threads.filter(
    (thread) => thread.suggestion_text && thread.suggestion_status === 'pending'
  ).length;
  const suggesting = mode === 'suggest';

  const safeBody = useMemo(() => {
    const raw = looksLikeHtml(body) ? body : `<p>${body || ''}</p>`;
    return sanitizeHtml(raw);
  }, [body]);

  const openThread = (id) => {
    setActiveId(id);
    setMobileTab('comments');
    requestAnimationFrame(() => {
      const node = sidebarRef.current?.querySelector(`[data-thread-id="${id}"]`);
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  };

  useEffect(() => {
    if (selectedCommentId) {
      openThread(selectedCommentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommentId]);

  useEffect(() => {
    if (focusComments) {
      setMobileTab('comments');
    }
  }, [focusComments]);

  useEffect(() => {
    if (pendingExcerpt) {
      setSelection({ excerpt: pendingExcerpt, start_offset: null, end_offset: null });
      setSuggestionDraft(pendingExcerpt);
      setComposerOpen(true);
    }
  }, [pendingExcerpt]);

  useLayoutEffect(() => {
    if (!bodyRef.current) {
      return;
    }
    applyThreadHighlights(bodyRef.current, threads, activeId);
  }, [threads, activeId, safeBody, composerOpen, selection, historyOpen]);

  const rememberSelection = () => {
    if (!canComment || !bodyRef.current) {
      return;
    }
    const next = captureTextSelection(bodyRef.current, documentRef.current);
    if (next?.excerpt) {
      setSelection(next);
      setSuggestionDraft(next.excerpt);
    }
  };

  useEffect(() => {
    if (!canComment) {
      return undefined;
    }
    const onSelectionChange = () => {
      rememberSelection();
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canComment]);

  const submit = async ({
    markReturned = false,
    parentId = null,
    fallbackComment = '',
  } = {}) => {
    if (!onSubmitComment) {
      return;
    }
    const commentText = (
      parentId ? replyDraft : draft || (markReturned ? fallbackComment : '')
    ).trim();
    const replacement = suggesting ? suggestionDraft.trim() : '';
    if (!commentText && !replacement) {
      return;
    }
    const excerpt = parentId ? '' : selection?.excerpt || '';
    await onSubmitComment({
      excerpt,
      comment: commentText || (replacement ? 'Suggested edit.' : ''),
      parent_id: parentId,
      start_offset: parentId ? null : selection?.start_offset ?? null,
      end_offset: parentId ? null : selection?.end_offset ?? null,
      mark_returned: markReturned,
      review_id: parentId
        ? threads.find((thread) => thread.id === parentId)?.review_id
        : null,
      assigned_user_id: parentId ? null : assignDraft ? Number(assignDraft) : null,
      suggestion_text: parentId ? null : replacement || null,
      mentioned_user_ids: mentionedIdsFromText(
        parentId ? replyDraft : draft,
        people
      ),
    });
    setDraft('');
    setReplyDraft('');
    setSuggestionDraft('');
    setAssignDraft('');
    setSelection(null);
    setComposerOpen(false);
    setReplyTo(null);
    if (typeof window !== 'undefined') {
      window.getSelection()?.removeAllRanges();
    }
  };

  const copyCommentLink = async (commentId) => {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('comment_id', String(commentId));
    try {
      await navigator.clipboard.writeText(url.toString());
      setLinkCopied(commentId);
      window.setTimeout(() => setLinkCopied(null), 1600);
    } catch (_err) {
      setLinkCopied(null);
    }
  };

  const selectionStyle = {
    top: Math.max(8, (selection?.top || 0) + 8),
    left: Math.max(8, Math.min(selection?.left || 8, 220)),
  };

  const composerFields = (isReply = false, placeholder) => (
    <>
      {!isReply && suggesting ? (
        <label className="rw-suggest-field">
          Replace with
          <textarea
            value={suggestionDraft}
            onChange={(event) => setSuggestionDraft(event.target.value)}
            placeholder="Suggested text"
          />
        </label>
      ) : null}
      <MentionField
        value={isReply ? replyDraft : draft}
        onChange={isReply ? setReplyDraft : setDraft}
        collaborators={people}
        placeholder={
          placeholder ||
          (isReply ? 'Reply to this comment' : 'Write a comment')
        }
      />
      {!isReply && people.length ? (
        <label className="rw-assign">
          Assign to
          <select value={assignDraft} onChange={(event) => setAssignDraft(event.target.value)}>
            <option value="">No one</option>
            {people.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </>
  );

  return (
    <div className={`rw is-${layout}`}>
      {error ? <p className="rw-error" role="alert">{error}</p> : null}
      <div className="rw-toolbar">
        <div className="rw-mode" role="group" aria-label="Editing mode">
          <button
            type="button"
            className={!suggesting ? 'is-active' : ''}
            aria-pressed={!suggesting}
            onClick={() => setMode('edit')}
          >
            Editing
          </button>
          <button
            type="button"
            className={suggesting ? 'is-active' : ''}
            aria-label="Suggesting"
            aria-pressed={suggesting}
            onClick={() => setMode('suggest')}
          >
            Suggesting
            {pendingSuggestions ? <span className="rw-badge">{pendingSuggestions}</span> : null}
          </button>
        </div>
        <button
          type="button"
          className="rw-toolbar__comments"
          onClick={() => {
            setMobileTab('comments');
            document
              .querySelector('.rw-sidebar')
              ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }}
        >
          <FiMessageCircle size={16} />
          Comments
          {unresolved ? <span className="rw-badge">{unresolved}</span> : null}
        </button>
        <button
          type="button"
          className={historyOpen ? 'is-active' : ''}
          onClick={() => {
            setHistoryOpen((open) => !open);
            setMobileTab('comments');
          }}
        >
          <FiClock size={16} />
          History
        </button>
        {canReturn ? (
          <button
            type="button"
            className="rw-toolbar__return"
            disabled={saving}
            onClick={() =>
              submit({
                markReturned: true,
                fallbackComment: 'Returned to student.',
              })
            }
          >
            {returningLabel}
          </button>
        ) : null}
      </div>
      <div className="rw-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mobileTab === 'document'}
          className={mobileTab === 'document' ? 'is-active' : ''}
          onClick={() => setMobileTab('document')}
        >
          Document
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobileTab === 'comments'}
          className={mobileTab === 'comments' ? 'is-active' : ''}
          onClick={() => setMobileTab('comments')}
        >
          Comments
          {unresolved ? <span className="rw-badge">{unresolved}</span> : null}
        </button>
      </div>

      <div className={`rw-layout is-${mobileTab} is-${layout}`}>
        <section ref={documentRef} className="rw-document" aria-label="Document">
          <div
            ref={bodyRef}
            className="rw-body"
            onMouseUp={rememberSelection}
            onClick={(event) => {
              const mark = event.target.closest?.('mark[data-comment-id]');
              if (mark) {
                openThread(Number(mark.dataset.commentId));
              }
            }}
            dangerouslySetInnerHTML={{ __html: safeBody }}
          />
          {selection && canComment && !composerOpen ? (
            <>
              <button
                type="button"
                className="rw-comment-icon"
                style={selectionStyle}
                aria-label={suggesting ? 'Suggest edit' : 'Add comment'}
                title={suggesting ? 'Suggest edit' : 'Add comment'}
                onClick={() => setComposerOpen(true)}
              >
                <FiMessageCircle size={18} />
              </button>
              <div className="rw-selection-bar">
                <p>“{selection.excerpt}”</p>
                <button type="button" onClick={() => setComposerOpen(true)}>
                  {suggesting ? 'Suggest' : 'Comment'}
                </button>
              </div>
            </>
          ) : null}
          {selection && canComment && composerOpen ? (
            <div className="rw-popover" style={selectionStyle}>
              <p className="rw-excerpt">“{selection.excerpt}”</p>
              {composerFields(false, 'Write a comment')}
              <div className="rw-popover__actions">
                <button
                  type="button"
                  onClick={() => {
                    setSelection(null);
                    setComposerOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="is-primary"
                  disabled={saving || (!draft.trim() && !(suggesting && suggestionDraft.trim()))}
                  onClick={() => submit()}
                >
                  {suggesting ? 'Suggest' : 'Comment'}
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <aside ref={sidebarRef} className="rw-sidebar" aria-label="Comments">
          <div className="rw-sidebar__head">
            <div>
              <p className="rw-kicker">Review thread</p>
              <h3>{historyOpen ? 'Version history' : 'Comments'}</h3>
            </div>
            {threads.length && !historyOpen ? (
              <span className={`rw-count${unresolved ? '' : ' is-quiet'}`}>
                {unresolved ? `${unresolved} open` : 'All resolved'}
              </span>
            ) : null}
          </div>

          {historyOpen ? (
            <div className="rw-history">
              {versionsLoading ? <p className="rw-empty">Loading versions…</p> : null}
              {!versionsLoading && !versions.length ? (
                <p className="rw-empty">No earlier versions yet. Save the document to create a snapshot.</p>
              ) : null}
              {versions.map((version) => (
                <article key={version.id} className="rw-version">
                  <div>
                    <strong>{version.created_by_name || 'Saved draft'}</strong>
                    <time dateTime={version.created_at}>{formatWhen(version.created_at)}</time>
                    {version.note ? <span className="rw-version__note">{version.note}</span> : null}
                  </div>
                  {onRestoreVersion ? (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => onRestoreVersion(version.id)}
                    >
                      Restore
                    </button>
                  ) : null}
                </article>
              ))}
            </div>
          ) : null}

          {!historyOpen && !threads.length ? (
            <p className="rw-empty">
              Select a sentence or paragraph to leave a comment.
            </p>
          ) : null}

          {!historyOpen
            ? threads.map((thread) => (
                <article
                  key={thread.id}
                  data-thread-id={thread.id}
                  className={`rw-thread${Number(activeId) === Number(thread.id) ? ' is-active' : ''}${
                    thread.resolved_at ? ' is-resolved' : ''
                  }${thread.suggestion_text ? ' is-suggestion' : ''}`}
                >
                  <div className="rw-thread__top">
                    <button
                      type="button"
                      className="rw-thread__open"
                      onClick={() => openThread(thread.id)}
                    >
                      {thread.excerpt ? (
                        <p className="rw-excerpt">“{thread.excerpt}”</p>
                      ) : (
                        <p className="rw-excerpt is-orphan">Whole document</p>
                      )}
                    </button>
                    <span className={`rw-status${thread.resolved_at ? ' is-done' : ''}`}>
                      {thread.suggestion_status === 'pending'
                        ? 'Suggestion'
                        : thread.suggestion_status === 'accepted'
                          ? 'Accepted'
                          : thread.suggestion_status === 'rejected'
                            ? 'Rejected'
                            : thread.resolved_at
                              ? 'Resolved'
                              : 'Open'}
                    </span>
                  </div>

                  <CommentMessage item={thread} />

                  {thread.suggestion_text ? (
                    <div className="rw-suggestion">
                      <p>
                        <span>Replace with</span>
                        {thread.suggestion_text}
                      </p>
                      {thread.suggestion_status === 'pending' && onAcceptSuggestion ? (
                        <div className="rw-suggestion__actions">
                          <button type="button" onClick={() => onRejectSuggestion?.(thread.id)}>
                            Reject
                          </button>
                          <button
                            type="button"
                            className="is-primary"
                            onClick={() => onAcceptSuggestion(thread.id)}
                          >
                            Accept
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {thread.assigned_user_name ? (
                    <p className="rw-assigned">Assigned to {thread.assigned_user_name}</p>
                  ) : null}

                  {(thread.replies || []).length ? (
                    <div className="rw-replies">
                      {(thread.replies || []).map((reply) => (
                        <CommentMessage key={reply.id} item={reply} />
                      ))}
                    </div>
                  ) : null}

                  <div className="rw-reactions" role="group" aria-label="Emoji reactions">
                    {REACTIONS.map((emoji) => {
                      const tally = (thread.reactions || []).find((row) => row.emoji === emoji);
                      const mine =
                        tally?.reacted ||
                        (currentUserId && (tally?.user_ids || []).includes(currentUserId));
                      return (
                        <button
                          key={emoji}
                          type="button"
                          className={mine ? 'is-on' : ''}
                          disabled={!onReact}
                          onClick={() => onReact?.(thread.id, emoji)}
                        >
                          {emoji}
                          {tally?.count ? <span>{tally.count}</span> : null}
                        </button>
                      );
                    })}
                  </div>

                  {canComment ? (
                    <div className="rw-thread__actions">
                      <button
                        type="button"
                        onClick={() => {
                          setReplyTo(thread.id);
                          setActiveId(thread.id);
                        }}
                      >
                        <FiCornerUpLeft size={14} /> Reply
                      </button>
                      <button type="button" onClick={() => copyCommentLink(thread.id)}>
                        <FiLink size={14} />
                        {linkCopied === thread.id ? 'Copied' : 'Copy link'}
                      </button>
                      {onAssign && people.length ? (
                        <label className="rw-assign-inline">
                          Assign
                          <select
                            value={thread.assigned_user_id || ''}
                            onChange={(event) =>
                              onAssign(
                                thread.id,
                                event.target.value ? Number(event.target.value) : null
                              )
                            }
                          >
                            <option value="">No one</option>
                            {people.map((person) => (
                              <option key={person.id} value={person.id}>
                                {person.name}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                      {thread.resolved_at ? (
                        <button type="button" onClick={() => onReopen?.(thread.id)}>
                          <FiRotateCcw size={14} /> Reopen
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="is-resolve"
                          onClick={() => onResolve?.(thread.id)}
                        >
                          <FiCheck size={14} /> Resolve
                        </button>
                      )}
                    </div>
                  ) : null}
                  {Number(replyTo) === Number(thread.id) ? (
                    <div className="rw-reply-box">
                      {composerFields(true, 'Reply to this comment')}
                      <div className="rw-reply-box__actions">
                        <button type="button" onClick={() => setReplyTo(null)}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="is-primary"
                          disabled={saving || !replyDraft.trim()}
                          onClick={() => submit({ parentId: thread.id })}
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  ) : null}
                </article>
              ))
            : null}

          {canComment && !selection && !historyOpen ? (
            <div className="rw-general">
              <label>
                <FiMessageCircle size={14} />{' '}
                {suggesting ? 'Suggest without a selection' : 'Comment without a selection'}
              </label>
              {composerFields(false, 'Your comment')}
              <div className="rw-general__actions">
                <button
                  type="button"
                  className="is-primary"
                  disabled={saving || (!draft.trim() && !(suggesting && suggestionDraft.trim()))}
                  onClick={() => submit()}
                >
                  {suggesting ? 'Add suggestion' : 'Add comment'}
                </button>
                {canReturn ? (
                  <button
                    type="button"
                    disabled={saving || !draft.trim()}
                    onClick={() => submit({ markReturned: true })}
                  >
                    {returningLabel}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
};

export default ReviewWorkspace;
